import * as React from "react";
import { Domain } from "../../schema/types/domain/entity";
import { linksOf, request } from "../../services/cpln";
import { Card } from "antd";
import { Loader } from "../../components/layout/loader";
import { CardContent } from "../../components/antd/card/CardContent";
import { CardTitle } from "../../components/antd/card/CardTitle";

interface DNSRecord {
  host: string;
  ttl: number;
  type: string;
  value: string;
}

interface DNSDetails {
  dnsConfig?: DNSRecord[];
  ownershipConfig?: DNSRecord[];
}

interface Props {
  domain: Domain;
}
export const DomainConfiguration: React.FC<Props> = ({ domain }) => {
  const selfLink = linksOf(domain).self!;

  const [gvcAlias, setGVCAlias] = React.useState("<gvcAlias>");
  const [details, setDetails] = React.useState<DNSDetails | null>(null);
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [unauthorized, setUnauthorized] = React.useState<boolean>(false);

  React.useEffect(() => {
    // Fetch GVC alias from the first workload route
    try {
      const workloadLink = (domain.spec?.ports[0].routes || [])[0]?.workloadLink;
      if (workloadLink) {
        const gvcLink = workloadLink.split("/workload")[0];
        request({ url: gvcLink })
          .then(({ data: gvcItem }) => {
            setGVCAlias(gvcItem.alias);
          })
          .catch(() => {});
      }
    } catch (e) {}
  }, [domain]);

  React.useEffect(() => {
    setIsLoading(true);
    setUnauthorized(false);

    // Make a dry run PATCH request to get DNS config
    // Modify description slightly to trigger validation
    const body: any = { ...domain, description: (domain.description || "") + (domain.description || "") };
    delete body.id;
    delete body.version;

    request({
      method: "patch",
      url: selfLink + "?dryRun",
      body,
    })
      .then(({ data }) => {
        // Response can have dnsConfig in data.details (success with message) or data.status (domain entity)
        const responseDetails = data.details || data.status;
        const dnsConfig = responseDetails?.dnsConfig;
        const ownershipConfig = responseDetails?.ownershipConfig;
        if (dnsConfig || ownershipConfig) {
          setDetails({ dnsConfig, ownershipConfig });
        } else {
          setDetails(null);
        }
        setIsLoading(false);
      })
      .catch((e) => {
        if (e.response?.status === 403) {
          setUnauthorized(true);
          setDetails(null);
        } else {
          // Extract details from error response
          const errorDetails = e?.response?.data?.details;
          if (errorDetails?.dnsConfig || errorDetails?.ownershipConfig) {
            setDetails(errorDetails);
          } else {
            setDetails(null);
          }
        }
        setIsLoading(false);
      });
  }, [domain, selfLink]);

  if (isLoading) {
    return <Loader />;
  }

  const ownershipRecords = details?.ownershipConfig || [];
  const dnsRecords = details?.dnsConfig || [];
  const allRecords = [...ownershipRecords, ...dnsRecords];
  const hasRecords = allRecords.length > 0;

  function renderRecord(record: DNSRecord, index: number) {
    const host = record.host === "@" ? domain.name : record.host;
    const value = record.value.replace("<gvcAlias>", gvcAlias);

    return (
      <div key={index} className="flex items-center gap-2 border px-2 py-1">
        <div className="w-5/12">{host}</div>
        <div className="w-1/12">{record.ttl.toString()}</div>
        <div className="w-1/12">{record.type}</div>
        <div className="w-5/12">{value}</div>
      </div>
    );
  }

  return (
    <div className="cpln-form-section cpln-ant-card-container" data-section-id={"dns"}>
      <Card id={"config"}>
        <CardTitle>DNS</CardTitle>
        <CardContent>
          {unauthorized ? (
            <div>You do not have permissions to view this information.</div>
          ) : !hasRecords ? (
            <div>The required DNS records will be available once routing is configured.</div>
          ) : (
            <>
              <div className="mb-2">Create the following DNS records:</div>
              <div className="flex flex-col gap-1 mb-4">
                <div>
                  If you have already made the change to DNS, please wait a few minutes. It takes time for DNS
                  propagation.
                </div>
              </div>
              <div className="mb-4">
                <div className="flex items-center gap-2 border px-2 py-1 table-labels">
                  <div className="w-5/12">Host</div>
                  <div className="w-1/12">TTL</div>
                  <div className="w-1/12">Type</div>
                  <div className="w-5/12">Value</div>
                </div>
                {ownershipRecords.map((r, index) => renderRecord(r, index))}
                {dnsRecords.map((r, index) => renderRecord(r, ownershipRecords.length + index))}
              </div>
              <div>
                After creating the above entries, it will take a few minutes for the changes to propagate across the
                Internet.
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
